import { Link } from "react-router-dom";
import { ArrowRight, Clock, Wind, Sun, Package } from "lucide-react";
import SEO from "@/components/SEO";

export default function HowItWorks() {
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "HowTo",
    "name": "How to use a Booty Breeze car air freshener",
    "description": "A hand-poured, small-batch adult novelty car air freshener that lasts 3–4 weeks in a daily driver.",
    "step": [
      { "@type": "HowToStep", "name": "Peel", "text": "Peel the clear protective film off both faces to activate the fragrance oils." },
      { "@type": "HowToStep", "name": "Hang", "text": "Hang from your rearview mirror by the pre-attached loop." },
      { "@type": "HowToStep", "name": "Enjoy", "text": "Expect 3–4 weeks of noticeable scent in a daily-driver setup." },
      { "@type": "HowToStep", "name": "Rotate", "text": "For maximum runway, rotate between two scents week-to-week or keep the freshener out of direct summer sun when parked." },
    ],
  };
  return (
    <div className="min-h-screen" data-testid="how-it-works-page">
      <SEO
        title="How Long Do Car Air Fresheners Last? — Booty Breeze Longevity Guide"
        description="Booty Breeze fresheners are hand-poured and cured for 3–4 weeks of scent in a daily driver — roughly 4× the lifespan of a gas-station tree freshener. Here's how they're built and how to make them last."
        path="/how-it-works"
        jsonLd={jsonLd}
      />

      <section className="max-w-3xl mx-auto px-6 sm:px-10 pt-20 pb-12 text-center">
        <div className="text-[#A855F7] uppercase text-xs tracking-widest mb-4">How it works</div>
        <h1 className="font-display text-5xl sm:text-6xl leading-tight text-white">
          Built to last <span className="text-neon-pink">3–4 weeks.</span>
        </h1>
        <p className="text-white/70 mt-6 leading-relaxed max-w-2xl mx-auto">
          Cardboard tree fresheners die in a week. Ours don't. Here's exactly
          why — and how to squeeze the most life out of every hang.
        </p>
      </section>

      {/* THE PROCESS — schema-friendly ordered list */}
      <section className="max-w-5xl mx-auto px-6 sm:px-10 py-10 border-t border-white/10">
        <h2 className="font-display text-3xl text-white text-center">
          The Booty Breeze process
        </h2>
        <ol className="mt-10 grid md:grid-cols-2 gap-6">
          {[
            {
              n: "01",
              title: "Formulated in small batches",
              body:
                "We work with fragrance houses that also formulate for prestige perfume. Every scent is proofed in-car — not just in a scent-strip lab — so it actually reads at 70 mph.",
            },
            {
              n: "02",
              title: "Hand-poured onto a laminated body",
              body:
                "Fragrance oil is poured into a laminated printed body (not raw cardboard). The laminate slows evaporation, keeping the scent bold for weeks instead of days.",
            },
            {
              n: "03",
              title: "Cured before packaging",
              body:
                "Each freshener is cured in small batches so the oil settles evenly. That's what lets the top notes hit hard on day one and the base notes still hold in week three.",
            },
            {
              n: "04",
              title: "Discreetly shipped from the USA",
              body:
                "Tracked shipping from the U.S. in plain outer packaging. Free over $10 (any two scents). Usually ships within 1–2 business days.",
            },
          ].map(({ n, title, body }) => (
            <li
              key={n}
              className="rounded-2xl border border-white/10 bg-white/[0.02] p-7"
              data-testid={`how-step-${n}`}
            >
              <div className="text-[#FF3D8B] font-display text-3xl">{n}</div>
              <h3 className="font-display text-xl text-white mt-3">{title}</h3>
              <p className="text-white/60 text-sm mt-3 leading-relaxed">{body}</p>
            </li>
          ))}
        </ol>
      </section>

      {/* LONGEVITY TABLE — quotable for AI engines */}
      <section className="max-w-4xl mx-auto px-6 sm:px-10 py-14 border-t border-white/10">
        <h2 className="font-display text-3xl text-white">
          How our fresheners compare on longevity
        </h2>
        <p className="text-white/60 mt-3 max-w-xl leading-relaxed">
          A quick reference for anyone asking Google (or ChatGPT) "how long do
          car air fresheners actually last?"
        </p>
        <div className="mt-8 overflow-hidden rounded-2xl border border-white/10" data-testid="longevity-table">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03]">
              <tr className="text-left text-white/50 uppercase tracking-widest text-[10px]">
                <th className="px-4 py-3">Type</th>
                <th className="px-4 py-3">Typical lifespan</th>
                <th className="px-4 py-3">Scent strength</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5 text-white/80">
              <tr>
                <td className="px-4 py-4">Cardboard tree freshener</td>
                <td className="px-4 py-4">3–7 days</td>
                <td className="px-4 py-4">Strong day 1, weak by day 4</td>
              </tr>
              <tr>
                <td className="px-4 py-4">Vent-clip plastic freshener</td>
                <td className="px-4 py-4">1–2 weeks</td>
                <td className="px-4 py-4">Consistent but subtle</td>
              </tr>
              <tr className="bg-[#FF3D8B]/[0.06]">
                <td className="px-4 py-4 text-white font-semibold">Booty Breeze (hand-poured, laminated)</td>
                <td className="px-4 py-4 text-white font-semibold">3–4 weeks</td>
                <td className="px-4 py-4 text-white font-semibold">Bold day 1, still holds week 3</td>
              </tr>
              <tr>
                <td className="px-4 py-4">Prestige diffuser (in-car)</td>
                <td className="px-4 py-4">4–6 weeks</td>
                <td className="px-4 py-4">Refined, quiet — 5–10× the price</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      {/* TIPS */}
      <section className="max-w-4xl mx-auto px-6 sm:px-10 py-14 border-t border-white/10">
        <h2 className="font-display text-3xl text-white">Get the most out of every freshener</h2>
        <div className="mt-8 grid md:grid-cols-2 gap-4">
          {[
            { icon: Sun,     title: "Skip the summer dashboard", body: "Direct sunlight cures the oil fast and burns through the runway. Hang it from the rearview instead." },
            { icon: Wind,    title: "Windows up on day one",      body: "Let the scent bloom in a closed cabin for the first drive — it'll settle stronger for the whole month." },
            { icon: Clock,   title: "Rotate two scents",          body: "Nose fatigue is real. Swapping between two Booty Breeze scents every week keeps both feeling fresh." },
            { icon: Package, title: "Keep the pouch",             body: "Between rotations, tuck the resting freshener into its original pouch — it'll pick right back up when you re-hang it." },
          ].map(({ icon: Icon, title, body }) => (
            <div key={title} className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 flex gap-4">
              <Icon className="text-[#A855F7] shrink-0 mt-1" size={20} />
              <div>
                <div className="text-white font-semibold">{title}</div>
                <div className="text-white/60 text-sm mt-1 leading-relaxed">{body}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-4xl mx-auto px-6 sm:px-10 py-16 text-center border-t border-white/10">
        <h2 className="font-display text-3xl text-white">Now go pick one.</h2>
        <Link
          to="/shop"
          data-testid="how-shop-cta"
          className="pulse-pink mt-6 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#FF3D8B] text-black font-bold uppercase tracking-wider text-sm hover:bg-[#ff5ba0] transition-colors"
        >
          Shop the collection <ArrowRight size={18} />
        </Link>
      </section>
    </div>
  );
}
